'use client';

import { Building2, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useId, useState } from 'react';

import { cn } from '../../lib/cn';

export interface ScopeColumnItem {
	/** Id univoco dell'ambito (CER o configurazione). */
	id: string;
	/** Nome della CER. */
	cerName: string;
	/** Nome della configurazione; nascosto quando identico a `cerName`. */
	configName: string;
	/** Soci attivi dell'ambito (registrati e non receduti), mostrati nell'intestazione. */
	sociCount: number;
	/** Contenuto del blocco (dati dell'ambito da confrontare). */
	content: React.ReactNode;
}

export interface ScopeColumnsViewProps {
	/** Ambiti da mostrare, uno per blocco. */
	items: ScopeColumnItem[];
}

/** Un blocco richiudibile per ambito, impilati verticalmente, per confrontare più CER/configurazioni affiancate. */
const ScopeColumnsView = ({ items }: ScopeColumnsViewProps) => (
	<div className="flex flex-col gap-6">
		{items.map((item, i) => (
			<ScopeBlock key={item.id} index={i + 1} item={item} />
		))}
	</div>
);
ScopeColumnsView.displayName = 'ScopeColumnsView';

function ScopeBlock({ index, item }: { index: number; item: ScopeColumnItem }) {
	const [open, setOpen] = useState(true);
	const panelId = useId();

	return (
		<section className="overflow-hidden rounded-[12px] border border-border">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				aria-expanded={open}
				aria-controls={panelId}
				className={cn(
					'flex w-full items-center gap-2 bg-panel px-4 py-2.5 text-left transition-colors hover:bg-card',
					open && 'border-b border-border',
				)}
			>
				<span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-accent/15 font-mono text-[13px] font-bold text-accent">
					{index}
				</span>
				<Building2 className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
				<span className="text-[14px] font-bold text-text-primary">{item.cerName}</span>
				{item.configName !== item.cerName && (
					<>
						<span className="text-text-muted">·</span>
						<span className="text-[14px] text-accent">{item.configName}</span>
					</>
				)}
				<span className="ml-auto font-mono text-[12px] text-text-muted">
					{item.sociCount > 0 ? `${item.sociCount} soci attivi` : 'nessun socio registrato'}
				</span>
				<ChevronDown
					className={cn(
						'h-4 w-4 shrink-0 text-text-muted transition-transform',
						!open && '-rotate-90',
					)}
					aria-hidden="true"
				/>
			</button>
			<div id={panelId} hidden={!open} className="p-4">
				{item.content}
			</div>
		</section>
	);
}

export { ScopeColumnsView };
